import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class NetworkService {
    private onlineStatusSubject = new BehaviorSubject<boolean>(navigator.onLine);
    public onlineStatus$ = this.onlineStatusSubject.asObservable();

    private showModalSubject = new BehaviorSubject<boolean>(false);
    public showModal$ = this.showModalSubject.asObservable();

    private modalMessageSubject = new BehaviorSubject<string>('');
    public modalMessage$ = this.modalMessageSubject.asObservable();

    private modalTitleSubject = new BehaviorSubject<string>('');
    public modalTitle$ = this.modalTitleSubject.asObservable();

    private modalTypeSubject = new BehaviorSubject<'error' | 'success'>('error');
    public modalType$ = this.modalTypeSubject.asObservable();

    private previousOnlineStatus: boolean = navigator.onLine;
    private modalTimer: any;
    private OFFLINE_STATUS_KEY = 'app_offline_status';

    private reloadAfterModalClose = false;

    constructor(private zone: NgZone) {
        this.initializeNetworkMonitoring();
        this.checkOfflineStateOnInit();
    }

    private initializeNetworkMonitoring(): void {
        this.zone.run(() => {
            const online$ = fromEvent(window, 'online').pipe(map(() => true));
            const offline$ = fromEvent(window, 'offline').pipe(map(() => false));
            const initial$ = of(navigator.onLine);

            const networkStatus$ = merge(online$, offline$, initial$).pipe(
                distinctUntilChanged(),
                debounceTime(100)
            );

            networkStatus$.subscribe({
                next: (status: boolean) => this.handleNetworkChange(status),
                error: (error) => console.error('Error monitoring network status:', error)
            });
        });
    }

    private checkOfflineStateOnInit(): void {
        const wasOffline = localStorage.getItem(this.OFFLINE_STATUS_KEY) === 'true';
        const isCurrentlyOffline = !navigator.onLine;

        if (isCurrentlyOffline) {
            setTimeout(() => {
                this.showNetworkModal(
                    'Connection Lost',
                    'Your internet connection has been interrupted.',
                    'error'
                );
            }, 500);
        } else if (wasOffline && !isCurrentlyOffline) {
            localStorage.removeItem(this.OFFLINE_STATUS_KEY);
        }
    }

    private handleNetworkChange(status: boolean): void {
        const previousStatus = this.previousOnlineStatus;
        this.previousOnlineStatus = status;
        this.onlineStatusSubject.next(status);

        if (!status) {
            localStorage.setItem(this.OFFLINE_STATUS_KEY, 'true');
        } else {
            localStorage.removeItem(this.OFFLINE_STATUS_KEY);
        }

        if (!status && previousStatus) {
            // Went offline
            this.showNetworkModal(
                'Connection Lost',
                'Your internet connection has been interrupted.',
                'error'
            );
        } 
        else if (status && !previousStatus) {
            // Came back online
            this.retryQueuedRequests();

            // 🔥 SET RELOAD FLAG
            this.reloadAfterModalClose = true;

            this.showNetworkModal(
                'Connection Restored',
                'Your internet connection has been restored.',
                'success'
            );

            if (this.modalTimer) {
                clearTimeout(this.modalTimer);
            }

            this.modalTimer = setTimeout(() => {
                this.hideModal();
            }, 3000);
        }
    }

    public retryQueuedRequests(): void {
        try {
            const queueKey = 'offline_requests_queue';
            const saved = localStorage.getItem(queueKey);

            if (saved) {
                const queue = JSON.parse(saved);
                if (queue.length > 0) {
                    localStorage.removeItem(queueKey);
                    // console.log(`Retrying ${queue.length} queued requests`);
                }
            }
        } catch (e) {
            console.warn('Failed to retry queued requests:', e);
        }
    }

    public showNetworkModal(
        title: string,
        message: string,
        type: 'error' | 'success'
    ): void {
        if (this.modalTimer) {
            clearTimeout(this.modalTimer);
            this.modalTimer = null;
        }

        this.zone.run(() => {
            this.modalTitleSubject.next(title);
            this.modalMessageSubject.next(message);
            this.modalTypeSubject.next(type);
            this.showModalSubject.next(true);
        });
    }

    public hideModal(): void {
        this.zone.run(() => {
            this.showModalSubject.next(false);

            if (this.modalTimer) {
                clearTimeout(this.modalTimer);
                this.modalTimer = null;
            }

            // 🔥 RELOAD AFTER MODAL CLOSE
            if (this.reloadAfterModalClose) {
                this.reloadAfterModalClose = false;

                setTimeout(() => {
                    window.location.reload();
                }, 100);
            }
        });
    }

    public get isOnline(): boolean {
        return this.onlineStatusSubject.value;
    }

    public checkNetworkStatus(): boolean {
        const status = navigator.onLine;
        this.onlineStatusSubject.next(status);

        if (!status) {
            localStorage.setItem(this.OFFLINE_STATUS_KEY, 'true');
        } else {
            localStorage.removeItem(this.OFFLINE_STATUS_KEY);
        }

        return status;
    }

    public checkAndShowOfflineModal(): void {
        const isOffline = !navigator.onLine;
        const wasOffline = localStorage.getItem(this.OFFLINE_STATUS_KEY) === 'true';

        if (isOffline || wasOffline) {
            this.showNetworkModal(
                'Connection Lost',
                'Your internet connection has been interrupted.',
                'error'
            );
        }
    }
}
